import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { JOURNAL_PHRASE_MODAL_Z_CLASS } from '@/lib/journal/journalOverlayLayers';
import { MODAL_TITLE_TYPE } from '@/lib/typography';
import type { JournalPhraseVisibility } from '@/utils/journalPhrases';
import { AccessibleOverlay } from './AccessibleOverlay';

export interface JournalPhraseFormValues {
  shortcut: string;
  title: string;
  body: string;
  visibility: JournalPhraseVisibility;
}

const EMPTY_FORM: JournalPhraseFormValues = {
  shortcut: '',
  title: '',
  body: '',
  visibility: 'private',
};

interface JournalPhraseFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: Partial<JournalPhraseFormValues>;
  pending?: boolean;
  canShare?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (values: JournalPhraseFormValues) => void;
}

export function JournalPhraseFormModal({
  open,
  mode,
  initial,
  pending = false,
  canShare = true,
  error,
  onClose,
  onSubmit,
}: JournalPhraseFormModalProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const visibilityName = useId();
  const [form, setForm] = useState<JournalPhraseFormValues>(EMPTY_FORM);

  useEffect(() => {
    if (!open) return;
    setForm({
      shortcut: initial?.shortcut ?? '',
      title: initial?.title ?? '',
      body: initial?.body ?? '',
      visibility: initial?.visibility ?? 'private',
    });
  }, [open, initial?.shortcut, initial?.title, initial?.body, initial?.visibility]);

  const canSave = Boolean(
    form.shortcut.trim() && form.body.trim() && !/\s/.test(form.shortcut.trim()),
  );

  return (
    <AccessibleOverlay
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      zClassName={JOURNAL_PHRASE_MODAL_Z_CLASS}
      panelClassName="mx-auto mt-12 max-w-md rounded-xl bg-ds-card shadow-pop"
    >
      <div className="flex items-center justify-between border-b border-ds-line p-4">
        <h3 id={titleId} className={MODAL_TITLE_TYPE}>
          {mode === 'create' ? t('journal.phrases.create') : t('journal.phrases.edit')}
        </h3>
        <Button
          type="button"
          variant="tertiary"
          size="icon"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <X />
        </Button>
      </div>
      <form
        className="space-y-3 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSave) return;
          onSubmit({
            shortcut: form.shortcut.trim(),
            title: form.title.trim(),
            body: form.body,
            visibility: canShare ? form.visibility : 'private',
          });
        }}
      >
        <Input
          label={t('journal.phrases.shortcut')}
          value={form.shortcut}
          onChange={(event) => setForm((prev) => ({ ...prev, shortcut: event.target.value }))}
          placeholder={t('journal.phrases.shortcut_placeholder')}
          hint={t('journal.phrases.shortcut_hint')}
        />
        <Input
          label={t('journal.phrases.name')}
          value={form.title}
          onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
          placeholder={t('journal.phrases.name_placeholder')}
        />
        <Textarea
          label={t('journal.phrases.body')}
          value={form.body}
          onChange={(event) => setForm((prev) => ({ ...prev, body: event.target.value }))}
          rows={6}
          placeholder={t('journal.phrases.body_placeholder')}
        />
        {canShare && (
          <fieldset className="flex gap-4 text-sm">
            <legend className="sr-only">{t('journal.phrases.visibility')}</legend>
            <label className="flex cursor-pointer items-center gap-1.5 text-ds-text">
              <input
                type="radio"
                name={visibilityName}
                checked={form.visibility === 'private'}
                onChange={() => setForm((prev) => ({ ...prev, visibility: 'private' }))}
                className="accent-ds-teal"
              />
              {t('journal.phrases.private')}
            </label>
            <label className="flex cursor-pointer items-center gap-1.5 text-ds-text">
              <input
                type="radio"
                name={visibilityName}
                checked={form.visibility === 'shared'}
                onChange={() => setForm((prev) => ({ ...prev, visibility: 'shared' }))}
                className="accent-ds-teal"
              />
              {t('journal.phrases.shared')}
            </label>
          </fieldset>
        )}
        {error && <p className="text-sm text-ds-red-ink">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!canSave || pending}>
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
          </Button>
        </div>
      </form>
    </AccessibleOverlay>
  );
}
